import * as React from 'react'

import { Box, Button, Icon } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { InjectHoverProvider, useHover } from 'modules/core/lib/hoverContext'
import i18n from 'modules/core/lib/i18n'
import type { ModalStates } from 'modules/core/lib/questionModalStates'

import CustomFieldsField from 'modules/custom_resources/store/models/customFields/Field'
import DuplicateQuestionConfigModal from 'modules/surveys/components/Form/QuestionConfigs/Duplicate'
import QuestionConfigModal from 'modules/surveys/components/Form/QuestionConfigs/Edit'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionConfig from 'modules/surveys/store/models/QuestionConfig'
import type Survey from 'modules/surveys/store/models/v2/Survey'

import Actions from './Actions'
import CustomField from './CustomField'
import QuestionPosition from './QuestionPosition'

type ActionsProps = React.ComponentProps<typeof Actions>

function DragAndDrop() {
  const isHover = useHover()

  return <>{isHover && <Icon.Medium icon="drag-and-drop" />}</>
}

interface Props {
  form: SurveyForm | Survey
  questionConfig: QuestionConfig
  updateQuestionConfig: (data: {
    question_config_id: number
    custom_field_params: { label: string; required: boolean; choice_options: Array<[]> }
    surveys_form_id: number
  }) => void
  toggleArchivedQuestionConfig: ActionsProps['toggleArchivedQuestionConfig']
  createQuestionConfigFromDuplicate: (data: unknown, id: number) => Promise<void>
  deleteQuestionConfig: (data: { id: number; surveys_form_id: number }) => Promise<void>
}

function Item({
  form,
  questionConfig,
  updateQuestionConfig,
  createQuestionConfigFromDuplicate,
  toggleArchivedQuestionConfig,
  deleteQuestionConfig,
}: Props) {
  const [openedModal, setOpenedModal] = React.useState<ModalStates>('none')
  const { custom_field, id, surveys_question_category_id } = questionConfig.attributes
  const field = new CustomFieldsField(custom_field)

  const { running: deleting, performAction: onDelete } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      await deleteQuestionConfig({
        id: id,
        surveys_form_id: form.id,
      })
      closePortal()
    }
  )

  const onEditSubmit = async (data: Record<string, unknown>) => {
    updateQuestionConfig({
      question_config_id: id,
      custom_field_params: {
        label: String(data.label || ''),
        required: Boolean(data.required),
        choice_options: Array.isArray(data.choice_options) ? data.choice_options : [],
      },
      surveys_form_id: form.id,
    })
  }

  const onDuplicateSubmit = (data: Record<string | number, unknown>) => {
    data['surveys_form_id'] = form.id

    return createQuestionConfigFromDuplicate(data, surveys_question_category_id)
  }

  return (
    <>
      <InjectHoverProvider>
        <Box flexDirection="row" paddingX="s16" flexGrow>
          <Box paddingTop="s24" width="s12">
            <DragAndDrop />
          </Box>
          <Box paddingTop="s24" paddingLeft="s16">
            <QuestionPosition questionConfig={questionConfig} />
          </Box>
          <Box paddingY="s24" paddingLeft="s24" gap="s8" flexGrow>
            <CustomField customField={custom_field} />
          </Box>
          <Box paddingTop="s24">
            <Actions
              form={form}
              questionConfig={questionConfig}
              setOpenedModal={setOpenedModal}
              toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
            />
          </Box>
        </Box>
      </InjectHoverProvider>

      {openedModal === 'view' && (
        <QuestionConfigModal
          readonly
          field={field}
          onClose={() => setOpenedModal('none')}
          onSubmit={onEditSubmit}
        />
      )}

      {openedModal === 'edit' && (
        <QuestionConfigModal
          field={field}
          onClose={() => setOpenedModal('none')}
          onSubmit={onEditSubmit}
        />
      )}

      {openedModal === 'duplicate' && (
        <DuplicateQuestionConfigModal
          field={field}
          onClose={() => setOpenedModal('none')}
          onSubmit={onDuplicateSubmit}
        />
      )}

      {openedModal === 'delete' && (
        <Modal
          title={i18n.t('surveys.form.question_configs.confirm_delete.title')}
          description={i18n.t('surveys.form.question_configs.confirm_delete.description')}
          illustration="server-edit"
          onClose={() => setOpenedModal('none')}
          onSubmit={onDelete}
          footer={
            <Button type="negative" disabled={deleting} submit>
              {i18n.t('surveys.form.question_configs.confirm_delete.submit')}
            </Button>
          }
        />
      )}
    </>
  )
}

export default observer(Item)
